/**
 * The open-file list, as a set of pure transitions.
 *
 * These are VS Code's tab rules, and they are here rather than inline in
 * `useMetadataEditorState` so they can be tested without mounting the IDE:
 *
 *   single click  → PREVIEW. Reuses the one preview slot, so clicking through a
 *                   list of files leaves one tab rather than twenty.
 *   double click  → PERMANENT. The preview is promoted and survives the next
 *                   single click.
 *   first edit    → PERMANENT. Typing is commitment; a tab holding unsaved work
 *                   must never be the tab that gets recycled.
 *
 * That last rule is what makes the preview slot safe. Because a dirty tab is
 * never a preview, "replace the preview" cannot discard edits — the invariant is
 * a consequence of the model instead of a check someone has to remember.
 *
 * Identity is the file id throughout (metadata UUID, or an `art_…` artifact id).
 * Names collide across metadata types; ids do not.
 */

export interface OpenFileLike {
  id: string;
  isPreview?: boolean;
}

/**
 * Open `entry` as a preview.
 *
 * Already open → untouched (the caller activates it; a click on an open file must
 * not reset its permanence or its content). Otherwise it takes over the existing
 * preview's position, or is appended when there is none.
 */
export function previewOpenFile<T extends OpenFileLike>(files: readonly T[], entry: T): T[] {
  if (files.some((f) => f.id === entry.id)) return files as T[];

  const preview = { ...entry, isPreview: true };
  const slot = files.findIndex((f) => f.isPreview);
  if (slot === -1) return [...files, preview];

  const next = [...files];
  next[slot] = preview;
  return next;
}

/**
 * Open `entry` permanently — a double-click, or a deliberate open by the agent
 * (an artifact, a refactor target).
 *
 * Already open as a preview → promoted in place, keeping its position and the
 * content the editor has already loaded for it.
 */
export function openFilePermanently<T extends OpenFileLike>(files: readonly T[], entry: T): T[] {
  const existing = files.findIndex((f) => f.id === entry.id);
  if (existing === -1) return [...files, { ...entry, isPreview: false }];
  if (!files[existing].isPreview) return files as T[];

  const next = [...files];
  next[existing] = { ...next[existing], isPreview: false };
  return next;
}

/**
 * Promote the tab for `fileId`, if it is a preview.
 *
 * Returns the same array reference when nothing changes, so callers can skip the
 * state write — this runs on every keystroke's dirty check.
 */
export function promoteOpenFile<T extends OpenFileLike>(files: readonly T[], fileId: string): T[] {
  const index = files.findIndex((f) => f.id === fileId);
  if (index === -1 || !files[index].isPreview) return files as T[];

  const next = [...files];
  next[index] = { ...next[index], isPreview: false };
  return next;
}
